// src/app/components/chat/ChatMessageBubble.tsx
//
// Bolha de mensagem individual da thread de chat interno. Memoizada
// (mesmo padrão de KanbanCard.tsx) — a thread pode ter centenas de
// mensagens renderizadas simultaneamente.

import React, { useState } from 'react';
import { Check, Download, Eye, Loader2, Pencil, Play, Smile, Trash2, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { chatService, type ChatAttachment, type ChatMessage, type ChatUserMini } from '../../services/chatService';
import { fileIconFor, getUserInitials, formatMessageTime } from './chatUtils';
import { ChatSharedCardBubble } from './ChatSharedCardBubble';
import { AudioBubblePlayer, MediaLightbox } from '../tabs/WhatsAppMediaPlayer';
import WhatsAppFormattedText from '../tabs/WhatsAppFormattedText';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];

/** Um anexo dentro da bolha — decide o modo de exibição pela categoria
 * (StorageFileMiniSerializer.category, calculada no backend a partir do
 * mime_type): preview inline pra imagem/vídeo/áudio/pdf, chip de download
 * genérico pros demais. Componente próprio (não inline no .map) porque
 * cada anexo tem seu próprio estado de lightbox/download. */
function ChatAttachmentBubble({ attachment, isOwn }: { attachment: ChatAttachment; isOwn: boolean }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { storage_file: file } = attachment;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await chatService.downloadAttachment(file.id, file.name);
    } catch {
      // erro silencioso — botão volta ao estado normal, usuário pode tentar de novo
    } finally {
      setDownloading(false);
    }
  };

  if (file.category === 'image' && file.url) {
    return (
      <>
        <button type="button" onClick={() => setLightboxOpen(true)} className="block">
          <img
            src={file.url}
            alt={file.name}
            className="rounded-lg max-w-[240px] max-h-64 object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
          />
        </button>
        <MediaLightbox
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          kind="image"
          src={file.url}
          downloadName={file.name}
        />
      </>
    );
  }

  if (file.category === 'video' && file.url) {
    return (
      <>
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="relative block rounded-lg overflow-hidden group max-w-[240px]"
        >
          <video src={file.url} className="max-w-[240px] pointer-events-none" preload="metadata" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <Play className="size-8 text-white drop-shadow" fill="white" />
          </span>
        </button>
        <MediaLightbox
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          kind="video"
          src={file.url}
          downloadName={file.name}
        />
      </>
    );
  }

  if (file.category === 'audio' && file.url) {
    return <AudioBubblePlayer src={file.url} isOutgoing={isOwn} />;
  }

  if (file.category === 'pdf' && file.url) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] max-w-xs overflow-hidden">
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-2.5 py-1.5 flex-1 min-w-0 hover:bg-slate-50 dark:hover:bg-white/[0.06]"
          title="Abrir PDF"
        >
          <Eye className="size-3.5 flex-shrink-0 text-red-500 dark:text-red-400" />
          <span className="text-xs text-slate-600 dark:text-slate-400 truncate">{file.name}</span>
        </a>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="p-1.5 border-l border-slate-200 dark:border-white/[0.08] hover:bg-slate-50 dark:hover:bg-white/[0.06] flex-shrink-0"
          title="Baixar"
        >
          {downloading ? (
            <Loader2 className="size-3.5 animate-spin text-slate-400 dark:text-slate-500" />
          ) : (
            <Download className="size-3.5 text-slate-400 dark:text-slate-500" />
          )}
        </button>
      </div>
    );
  }

  const { Icon, className } = fileIconFor(file.mime_type, file.name);
  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-slate-50 dark:hover:bg-white/[0.06] text-left max-w-xs"
    >
      {downloading ? (
        <Loader2 className="size-3.5 flex-shrink-0 animate-spin text-slate-400 dark:text-slate-500" />
      ) : (
        <Icon className={`size-4 flex-shrink-0 ${className}`} />
      )}
      <span className="text-xs text-slate-600 dark:text-slate-400 truncate">{file.name}</span>
      {!downloading && <Download className="size-3 flex-shrink-0 text-slate-300 dark:text-slate-600" />}
    </button>
  );
}

export interface ChatMessageBubbleProps {
  message: ChatMessage;
  currentUserId: number;
  /** manager/coordinator podem apagar mensagens de qualquer autor */
  canDeleteAny: boolean;
  /** Quem já leu esta mensagem (calculado em ChatThread) — só preenchido
   * na mensagem mais recente lida por cada pessoa, pra não poluir a thread. */
  readBy?: ChatUserMini[];
  onEdit: (messageId: string, content: string) => void;
  onDelete: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}

export const ChatMessageBubble = React.memo(function ChatMessageBubble({
  message,
  currentUserId,
  readBy,
  canDeleteAny,
  onEdit,
  onDelete,
  onToggleReaction,
}: ChatMessageBubbleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);

  const isOwn = message.author?.id === currentUserId;
  const canEdit = isOwn && !message.is_deleted;
  const canDelete = (isOwn || canDeleteAny) && !message.is_deleted;

  const handleSaveEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === message.content) {
      setIsEditing(false);
      setDraft(message.content);
      return;
    }
    onEdit(message.id, trimmed);
    setIsEditing(false);
  };

  return (
    <div className="group flex gap-2.5 px-4 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.03] rounded-lg">
      <Avatar className="size-8 mt-0.5 flex-shrink-0">
        {message.author?.avatar_url && <AvatarImage src={message.author.avatar_url} alt="" />}
        <AvatarFallback className="bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 text-[11px] font-semibold">
          {getUserInitials(message.author)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {message.author?.full_name ?? 'Usuário removido'}
          </span>
          <span className="text-[11px] text-slate-400 dark:text-slate-500">{formatMessageTime(message.created_at)}</span>
          {message.edited_at && !message.is_deleted && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">(editado)</span>
          )}

          {!message.is_deleted && (
            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-white/10 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                    title="Reagir"
                  >
                    <Smile className="size-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-1.5 flex gap-1" align="end">
                  {QUICK_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => onToggleReaction(message.id, emoji)}
                      className="text-base p-1 rounded hover:bg-slate-100 dark:hover:bg-white/10"
                    >
                      {emoji}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              {canEdit && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="p-1 rounded hover:bg-slate-200 dark:hover:bg-white/10 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                  title="Editar"
                >
                  <Pencil className="size-3.5" />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(message.id)}
                  className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-500/15 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                  title="Apagar"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="mt-1 space-y-1.5">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                }
                if (e.key === 'Escape') {
                  setIsEditing(false);
                  setDraft(message.content);
                }
              }}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveEdit}
                className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
              >
                <Check className="size-3.5" /> Salvar
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setDraft(message.content);
                }}
                className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              >
                <X className="size-3.5" /> Cancelar
              </button>
            </div>
          </div>
        ) : message.is_deleted ? (
          <p className="text-sm italic text-slate-400 dark:text-slate-500 mt-0.5">{message.content}</p>
        ) : message.content && !message.card_share ? (
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5 whitespace-pre-wrap break-words">
            {/* Links clicáveis e negrito/itálico do WhatsApp — inclusive o "Abrir o
                atendimento" das mensagens encaminhadas da Central. */}
            <WhatsAppFormattedText text={message.content} />
          </p>
        ) : null}

        {message.card_share && (
          // Legenda (message.content) renderizada DENTRO da bolha do card,
          // não como parágrafo solto acima — grupamento visual deixa claro
          // que é uma legenda deste card, não uma mensagem independente.
          <ChatSharedCardBubble
            messageId={message.id}
            share={message.card_share}
            caption={!message.is_deleted ? message.content : undefined}
          />
        )}

        {message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1.5 items-start">
            {message.attachments.map((att) => (
              <ChatAttachmentBubble key={att.id} attachment={att} isOwn={isOwn} />
            ))}
          </div>
        )}

        {message.reactions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.reactions.map((reaction) => {
              const reactedByMe = reaction.user_ids.includes(currentUserId);
              return (
                <button
                  key={reaction.emoji}
                  type="button"
                  onClick={() => onToggleReaction(message.id, reaction.emoji)}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    reactedByMe
                      ? 'bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-300'
                      : 'bg-slate-50 dark:bg-white/[0.03] border-slate-200 dark:border-white/[0.08] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/[0.06]'
                  }`}
                >
                  <span>{reaction.emoji}</span>
                  <span>{reaction.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {readBy && readBy.length > 0 && (
          <div className="mt-1 flex items-center gap-1">
            <div className="flex -space-x-1.5">
              {readBy.slice(0, 5).map((u) => (
                <Avatar key={u.id} className="size-4 ring-2 ring-white dark:ring-[#0a0b0d]" title={`Visto por ${u.full_name}`}>
                  {u.avatar_url && <AvatarImage src={u.avatar_url} alt="" />}
                  <AvatarFallback className="bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-400 text-[7px] font-semibold">
                    {getUserInitials(u)}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            {readBy.length > 5 && <span className="text-[10px] text-slate-400 dark:text-slate-500">+{readBy.length - 5}</span>}
          </div>
        )}
      </div>
    </div>
  );
});
